import { debounce, safeOpenUrl, previewFile } from '@/utils/api';
import { resolveStoreId, resolvePageId } from '@rebuild/utils/context';
import { IRSTORE_T_CLICK } from '@/utils/track';
import { useNavigation } from '@rebuild/hooks/useNavigation';
import { useRouter, useStore } from '@/utils/vue-api';
import { computed } from 'vue';
import { enterprise } from '@/axios/interface';
import instance from '@/axios';
import { Message } from 'irm-ui';
import { previewAnnounce, previewCustom } from '@rebuild/utils/navigator';
import i18n from '@/locales';
import { SCHEDULE_TYPE, SCHEDULE_CUSTOMS_TYPE } from '@/hooks/calendar/useScheduleCore';
import { useCommonData } from '@/hooks';

const getReportDetail = async (reportId, reportType) => {
	return instance
		.post(enterprise.researchReportDetail(), {
			reportId: reportId,
			reportType: reportType
		})
		.then(response => {
			return response.data?.data || {};
		})
		.catch(() => {
			return {};
		});
};

const useNavigateStore = () => {
	const store = useStore();
	const isMobile = computed(() => store.getters.isMobileMode);

	const router = useRouter();
	const { buildTemplatePath, buildTemplateQuery } = useNavigation();

	const navigateToActivity = item => {
		const { businessTypes, url, descUrl } = item;
		let realUrl;
		// businessTypes 3:调研纪要 8:路演活动 9:调研活动
		if (businessTypes === 3) {
			realUrl = url;
		} else if (businessTypes === 8 || businessTypes === 9) {
			realUrl = descUrl;
		}
		if (!realUrl) return;

		if (businessTypes === 3) return navigateToResearch(item);
		return handleActivity(item);
	};
	const handleActivity = item => {
		const { id, descUrl, sourceType } = item;
		if (!descUrl) {
			Message.error('暂无活动文件');
			return;
		}

		// 自主添加
		if (sourceType === 1) {
			safeOpenUrl(descUrl);
		} else {
			safeOpenUrl(descUrl, { lang: i18n.locale });
		}
	};
	const navigateToRoadshow = item => handleActivity(item);
	const navigateToOffline = item => handleActivity(item);
	const navigateToResearch = item => {
		const { id, url } = item;
		if (!url) {
			Message.error('暂无活动文件');
			return;
		}

		var pattern = /comein-files\.oss-cn-hangzhou\.aliyuncs\.com/;
		var reg = pattern.test(url);
		if (reg) return previewFile(url);

		safeOpenUrl(url);
	};

	const { isFlowXTmpl, isSlideTmpl, isSurfTmpl } = useCommonData();
	const shouldOpenInWindow = () => {
		if (isMobile.value) return false;
		// 仅在PC侧，由于菜单栏、导航栏的存在，才需要打开新窗口
		return isFlowXTmpl.value || isSlideTmpl.value || isSurfTmpl.value;
	};

	const navigateToNews = item => {
		if (item.url) {
			safeOpenUrl(item.url);
			return;
		}

		const params = {
			path: buildTemplatePath('news/detail'),
			query: buildTemplateQuery({
				id: item.id
			})
		};

		const shouldOpenWindow = shouldOpenInWindow();
		if (shouldOpenWindow) {
			const url = router.resolve(params);
			window.open(url.href);
		} else {
			router.push(params);
		}
	};

	const navigateToReport = async item => {
		if (item.url) {
			// 有返回研报链接 直接打开链接
			return previewFile(item.url);
		}

		// 获取研报纪要
		const detailResult = await getReportDetail(item.reportId, item.type);
		// 是否返回conclusion
		const { conclusion } = detailResult;

		if (!conclusion) {
			return Message.error('研报内容丢失');
		}

		const params = {
			path: buildTemplatePath('report/detail'),
			query: buildTemplateQuery({
				id: item.reportId,
				type: item.type
			})
		};

		const shouldOpenWindow = shouldOpenInWindow();
		if (shouldOpenWindow) {
			const url = router.resolve(params);
			window.open(url.href);
		} else {
			router.push(params);
		}
	};

	const navigateToAnnouncement = item => previewAnnounce(item);
	const navigateToUsAnnouncement = item => safeOpenUrl(item.url);

	const navigateToCalendar = item => {
		const { type, customsType, investmentActivityType, investmentActivityVO, announcementVO, newsVO } = item;
		if (type === SCHEDULE_TYPE.CUSTOMS_CALENDAR) {
			if (customsType === SCHEDULE_CUSTOMS_TYPE.IRSTORE) {
				if (investmentActivityType === 3) {
					navigateToResearch(investmentActivityVO);
				} else {
					if (investmentActivityVO?.descUrl) handleActivity(investmentActivityVO);
				}
			}
			if (customsType === SCHEDULE_CUSTOMS_TYPE.ANNOUNCEMENT && announcementVO) {
				navigateToAnnouncement(announcementVO);
			}
			if (customsType === SCHEDULE_CUSTOMS_TYPE.NEWS && newsVO) {
				navigateToNews(newsVO);
			}
		}
	};

	const navigateToPerformanceReport = item => previewAnnounce(item);
	const navigateToCustom = item => previewCustom(item);

	const navigateToQuestion = item => {
		const params = {
			path: buildTemplatePath('question/detail'),
			query: buildTemplateQuery({
				uniqueCode: item.uniqueCode,
				sourceBiz: item.sourceBiz
			})
		};
		router.push(params);
	};

	const navigateToEsg = item => {
		if (item.fileUrl) {
			previewFile(item.fileUrl);
		} else if (item.url) {
			window.open(item.url, '_blank');
		}
	};

	const navigateToQuarterlyPerformanceRoadshow = item => {
		const { roadshowLink } = item;
		if (roadshowLink) safeOpenUrl(roadshowLink);
	};
	const navigateToQuarterlyPerformanceImagePreview = ({ url }) => {
		if (!url) return;
		if (isFlowXTmpl.value) {
			// 直接打开新窗口
			window.open(url, '_blank');
			return;
		}
		router.push({
			name: 'enterprise_preview',
			query: {
				imageUrl: url
			}
		});
	};
	const navigateToQuarterlyPerformanceAnnouncement = item => previewAnnounce(item);
	const navigateToQuarterlyPerformanceSummary = item => previewAnnounce(item);
	const navigateToQuarterlyPerformanceCustom = item => previewAnnounce(item);

	const navigateToCorporateGovernance = item => previewCustom(item);

	const storeNavigators = {
		investment_activity: navigateToActivity,
		roadshow: navigateToRoadshow,
		offline: navigateToOffline,
		research: navigateToResearch,
		announcement: navigateToAnnouncement,
		us_announcement: navigateToUsAnnouncement,
		report: navigateToReport,
		news: navigateToNews,
		customs_calendar: navigateToCalendar,
		performance_report: navigateToPerformanceReport,
		custom: navigateToCustom,
		question: navigateToQuestion,
		esg: navigateToEsg,

		quarterly_performance_roadshow: navigateToQuarterlyPerformanceRoadshow,
		quarterly_performance_image_preview: navigateToQuarterlyPerformanceImagePreview,
		quarterly_performance_announcement: navigateToQuarterlyPerformanceAnnouncement,
		quarterly_performance_summary: navigateToQuarterlyPerformanceSummary,
		quarterly_performance_custom: navigateToQuarterlyPerformanceCustom,

		corporate_governance: navigateToCorporateGovernance
	};

	return {
		storeNavigators
	};
};

export function useModuleNavigation() {
	const { storeNavigators } = useNavigateStore();

	const moduleNavigate = (moduleId, ...rest) => {
		const navigator = storeNavigators[moduleId];
		if (!navigator) {
			console.warn(`未找到模块 ${moduleId} 的跳转处理函数`);
			return;
		}
		return navigator(...rest);
	};

	const viewModule = debounce((moduleId, ...rest) => {
		irTrackSend(moduleId, ...rest);
		return moduleNavigate(moduleId, ...rest);
	}, 300);

	return {
		viewModule
	};
}

const storeNames = {
	investment_activity: '投关活动',
	roadshow: '路演活动',
	offline: '调研活动',
	research: '调研纪要',
	announcement: '公司公告',
	us_announcement: '美股公告',
	report: '券商研报',
	news: '新闻资讯',
	customs_calendar: '投关日历',
	performance_report: '业绩报告',
	custom: null,
	question: '互动问答',
	esg: 'ESG',

	quarterly_performance_roadshow: '季度业绩/业绩路演',
	quarterly_performance_image_preview: '季度业绩/一图读懂',
	quarterly_performance_announcement: '季度业绩/业绩公告',
	quarterly_performance_summary: '季度业绩/业绩摘要',
	quarterly_performance_custom: '季度业绩/业绩演示',

	corporate_governance: '企业管治'
};
const irTrackSend = (moduleId, item, ...rest) => {
	const storeId = resolveStoreId();
	const pageId = resolvePageId();
	if (!storeId || !pageId) return;

	const { id, ...restP } = item || {};

	const trackParams = {
		内容ID: id,
		企业ID: storeId,
		页面ID: pageId
	};

	let contentTitle;
	if (moduleId === 'custom') {
		contentTitle = rest?.[0] || '自定义模块';
	} else {
		contentTitle = storeNames[moduleId];
	}
	trackParams['点击内容名称'] = contentTitle;

	let selfContent = '自定义添加内容';
	if (moduleId === 'roadshow' || moduleId === 'offline') {
		if (restP.sourceType !== 1) selfContent = '默认内容';
	}
	if (moduleId === 'investment_activity') {
		if (restP.businessTypes === 8 || restP.businessTypes === 9) {
			if (restP.sourceType !== 1) selfContent = '默认内容';
		}
	}
	trackParams['点击内容类型'] = selfContent;

	IRSTORE_T_CLICK(283, {
		params: trackParams
	});
};
